import type { TeamId } from "@arcade-hockey/shared";
import { type MatchState, tuning } from "@arcade-hockey/sim";

// Phase G slice 2 — situation classifier. Pure function of state + my team
// id. Six situations across (zone × ownership):
//
//   - OffZoneOurPuck     we have puck in their end (cycle/shoot)
//   - OffZoneTheirPuck   they have puck in their end (forecheck)
//   - NeutralZoneOurPuck   transition forward
//   - NeutralZoneTheirPuck backcheck through NZ
//   - DefZoneOurPuck     breakout
//   - DefZoneTheirPuck   defensive-zone defense
//
// Loose puck classifies as "theirs" — conservative default so non-chaser
// bots fall back rather than push forward. The closest-chaser overlay
// still races for the puck, so we lose nothing by being defensive on the
// off-puck shape.
//
// Blue-line hysteresis: the puck must cross the blue line by
// `tuning.positioning.zones.zoneHysteresis` meters to flip the zone for
// this team. Without it, a puck oscillating at the blue line would whip
// non-chasers between "forecheck" and "neutral support" each tick and
// cause visible jitter.

export type Situation =
  | "OffZoneOurPuck"
  | "OffZoneTheirPuck"
  | "NeutralZoneOurPuck"
  | "NeutralZoneTheirPuck"
  | "DefZoneOurPuck"
  | "DefZoneTheirPuck";

// Per-team last-classified zone, for hysteresis. Keyed by TeamId so the
// HOME/AWAY classifications evolve independently. Module-scoped so the
// state persists across ticks; consequently this module is *not* pure
// (last-seen zone leaks across calls). That's intentional — the hysteresis
// state is a UI-smoothing concern, not a determinism-affecting one, and
// the values aren't mixed into the hash.
//
// If situation flicker turns out to matter for determinism in later slices
// (e.g. job dispatch becomes hash-affecting), this state will need to
// move onto MatchState.
type ZoneCode = "off" | "nz" | "def";
const LAST_ZONE: Record<TeamId, ZoneCode> = { home: "nz", away: "nz" };

function classifyZone(myTeam: TeamId, puckX: number): ZoneCode {
  const attackDir = myTeam === "home" ? 1 : -1;
  const projected = puckX * attackDir;
  const blue = tuning.positioning.zones.blueLineX;
  const hyst = tuning.positioning.zones.zoneHysteresis;
  const last = LAST_ZONE[myTeam];

  // Hysteresis: only switch when projected x crosses the blue line by
  // `hyst` past the boundary. Keeps the zone stable when puck dances at
  // the line.
  if (last === "off") {
    if (projected < blue - hyst) {
      LAST_ZONE[myTeam] = projected < -blue + hyst ? "def" : "nz";
    }
  } else if (last === "def") {
    if (projected > -blue + hyst) {
      LAST_ZONE[myTeam] = projected > blue - hyst ? "off" : "nz";
    }
  } else {
    if (projected > blue + hyst) LAST_ZONE[myTeam] = "off";
    else if (projected < -blue - hyst) LAST_ZONE[myTeam] = "def";
  }
  return LAST_ZONE[myTeam];
}

function classifyOwnership(state: MatchState, myTeam: TeamId): "ours" | "theirs" {
  if (state.puck.ownerId === null) return "theirs";
  const owner = state.playersById.get(state.puck.ownerId);
  if (owner === undefined) return "theirs";
  return owner.teamId === myTeam ? "ours" : "theirs";
}

export function classifySituation(state: MatchState, myTeam: TeamId): Situation {
  const zone = classifyZone(myTeam, state.puck.pos.x);
  const owner = classifyOwnership(state, myTeam);
  if (zone === "off") return owner === "ours" ? "OffZoneOurPuck" : "OffZoneTheirPuck";
  if (zone === "def") return owner === "ours" ? "DefZoneOurPuck" : "DefZoneTheirPuck";
  return owner === "ours" ? "NeutralZoneOurPuck" : "NeutralZoneTheirPuck";
}

// Test seam. Reset the hysteresis cache to its initial state so unit tests
// can exercise the classifier without seeing state leaked from a previous
// test run.
export function resetSituationCache(): void {
  LAST_ZONE.home = "nz";
  LAST_ZONE.away = "nz";
}
